/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { PlaybackContentType, PlaybackRestrictionType } from "../enums";
import type { IMetadata, IPlayable, IRestrictionManager } from "../interfaces";
import type { ISubscription } from "../ISubscription";
import { SubscriptionModel } from "../SubscriptionModel";
import { Log, Diagnostics } from "../../logging";
import { NetworkAvailabilityServiceImpl } from "../../network";
import { NetworkResource } from "../../enums";
import type { INetworkResourceListener } from "../../interfaces";
import type { INetworkResourceAvailability } from "../../types";
import type { TInHomeRestrictionParams } from "../types";
import {
  InHomeLocationRestrictionImpl,
  MetadataRestrictionImpl
} from "../impls";
import { PostSigninInteractor } from "../../interactor";

/**
 * Skeleton RestrictionManager implementing IRestrictionManager.
 * Runs the restriction pipeline (metadata restrictions + in-home location).
 * Full implementation deferred to Phase 6/7.
 *
 * VOD carries no channel, so no station policy applies to it. The in-home
 * step yields the service status (error / unreachable), BLACKLISTED,
 * OUT_OF_REGION, or OUT_OF_HOME whenever the device is not in home — VOD is
 * in-home only, with no policy that could allow it outside. The metadata
 * step yields only the watch action's device-type restriction. Adult /
 * parental-control and entitlement checks for VOD happen before playback is
 * requested.
 */
export class RestrictionManager
  implements IRestrictionManager, INetworkResourceListener
{
  private static readonly TAG: string = "RestrictionManager";

  private mRestrictionType: PlaybackRestrictionType =
    PlaybackRestrictionType.NO_RESTRICTION;

  private mSubscription: SubscriptionModel<PlaybackRestrictionType> =
    new SubscriptionModel<PlaybackRestrictionType>();

  private oInHomeLocationRestrictionChecker: InHomeLocationRestrictionImpl;

  private oMetadataRestrictionChecker: MetadataRestrictionImpl;

  private static inHomeLocationInRecovery: boolean = false;

  private mBoundVisibilityChange: () => void;

  public constructor() {
    this.mBoundVisibilityChange = this.handleVisibilityChange.bind(this);

    document.removeEventListener(
      "visibilitychange",
      this.mBoundVisibilityChange
    );
    document.addEventListener("visibilitychange", this.mBoundVisibilityChange);
    NetworkAvailabilityServiceImpl.getInstance().addListener(
      NetworkResource.INTERNET,
      this
    );

    this.oInHomeLocationRestrictionChecker =
      new InHomeLocationRestrictionImpl();
    this.oMetadataRestrictionChecker = new MetadataRestrictionImpl();
  }

  private log(
    level: "debug" | "info" | "warn" | "error",
    message: string,
    ...optionalParams: unknown[]
  ): void {
    Diagnostics[level](RestrictionManager.TAG, message, ...optionalParams);
    Log[level[0] as "d" | "i" | "w" | "e"](
      RestrictionManager.TAG,
      message,
      ...optionalParams
    );
    if (level === "error") {
      Diagnostics.error(
        RestrictionManager.TAG,
        "stacktrace:,",
        optionalParams[0]
      );
    }
  }

  private async checkRestriction(
    playable: IPlayable,
    metadata?: IMetadata
  ): Promise<PlaybackRestrictionType> {
    const networkService = NetworkAvailabilityServiceImpl.getInstance();
    const internetAvailability = networkService.getAvailability(
      NetworkResource.INTERNET
    );
    if (internetAvailability.isAvailable !== true) {
      this.mRestrictionType = PlaybackRestrictionType.NETWORK_DISCONNECTED;
      return this.mRestrictionType;
    }

    const inHomeRestriction: PlaybackRestrictionType =
      await this.oInHomeLocationRestrictionChecker.checkRestriction({
        playbackContentType: playable.playbackContentType,
        stationPolicy: RestrictionManager.getStationPolicy(playable)
      });

    /**
     * according to the usecase, if service is inhome location service is unreachable,
     * check for other restrictions. if no other restriction is found, send restriction
     * as INHOME_LOCATION_UNREACHABLE. to achive that this.mRestrictionType is assigned
     * the value of inHomeLocationRestriction after checking for all other restrictions.
     */

    if (
      inHomeRestriction === PlaybackRestrictionType.INHOME_LOCATION_UNREACHABLE
    ) {
      if (!RestrictionManager.inHomeLocationInRecovery) {
        RestrictionManager.inHomeLocationInRecovery = true;
        const services = new PostSigninInteractor();
        services
          .recoverInHomeLocationInfo()
          .then(() => {
            RestrictionManager.inHomeLocationInRecovery = false;
            this.handleInhomeLocationRecovery(playable, metadata);
          })
          .catch((error: unknown) => {
            RestrictionManager.inHomeLocationInRecovery = false;
            this.log("error", "recoverInHomeLocationInfo()", error);
          });
      }
    }

    if (
      inHomeRestriction !== PlaybackRestrictionType.NO_RESTRICTION &&
      inHomeRestriction !== PlaybackRestrictionType.INHOME_LOCATION_UNREACHABLE
    ) {
      this.mRestrictionType = inHomeRestriction;
      return this.mRestrictionType;
    }

    const metadataRestriction: PlaybackRestrictionType =
      await this.oMetadataRestrictionChecker.checkRestriction({
        playable,
        metadata
      });
    if (metadataRestriction !== PlaybackRestrictionType.NO_RESTRICTION) {
      this.mRestrictionType = metadataRestriction;
      return this.mRestrictionType;
    }

    this.mRestrictionType = inHomeRestriction;
    return this.mRestrictionType;
  }

  /**
   * Station policy governing the playable's location rules. VOD has none —
   * it is not channel-bound, even when a caller attaches a channel for
   * display purposes — so its policy is always `undefined`.
   */
  private static getStationPolicy(
    playable: IPlayable
  ): TInHomeRestrictionParams["stationPolicy"] {
    if (playable.playbackContentType === PlaybackContentType.VOD) {
      return undefined;
    }
    return playable.channel !== null && playable.channel !== undefined
      ? playable.channel.policyInfo
      : undefined;
  }

  private async handleInhomeLocationRecovery(
    playable: IPlayable,
    metadata?: IMetadata
  ) {
    this.mRestrictionType = await this.checkRestriction(playable, metadata);
    this.mSubscription.notify(this.mRestrictionType);
  }

  public onNetworkResourceAvailabilityChanged(
    _resource: NetworkResource,
    availability: INetworkResourceAvailability
  ): void {
    this.handleNetworkChange(availability);
  }

  private handleNetworkChange(
    availability: INetworkResourceAvailability
  ): void {
    if (availability.isAvailable === true) {
      this.mRestrictionType = PlaybackRestrictionType.NETWORK_CONNECTED;
    } else {
      this.mRestrictionType = PlaybackRestrictionType.NETWORK_DISCONNECTED;
    }
    this.mSubscription.notify(this.mRestrictionType);
  }

  private handleVisibilityChange(): void {
    if (document.hidden) {
      this.mRestrictionType = PlaybackRestrictionType.APP_BACKGROUND;
    } else {
      this.mRestrictionType = PlaybackRestrictionType.APP_FOREGROUND;
    }

    this.mSubscription.notify(this.mRestrictionType);
  }

  // ── IRestrictionManager ────────────────────────────────────────────────────

  public async checkAllRestriction(
    playable: IPlayable,
    metadata: IMetadata
  ): Promise<PlaybackRestrictionType> {
    const restrictionType = await this.checkRestriction(playable, metadata);
    this.log("debug", "checkAllRestriction() - " + restrictionType);
    return restrictionType;
  }

  public async checkCanPlayStartRestriction(
    playable: IPlayable
  ): Promise<PlaybackRestrictionType> {
    const restrictionType = await this.checkRestriction(playable);
    this.log("debug", "checkCanPlayStartRestriction() - " + restrictionType);
    return restrictionType;
  }

  public getRestrictionListener(): ISubscription<PlaybackRestrictionType> {
    return this.mSubscription;
  }

  public getRestrictionType(): PlaybackRestrictionType {
    return this.mRestrictionType;
  }
}
